import type { AuditLog } from "@aiyo/shared";
import { ClockArrowRotateLeft } from "@gravity-ui/icons";
import { Button, Card, Chip, Drawer, Table } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { FilterSelect } from "../components/filters";
import { Code, EmptyBlock, ErrorBlock, KeyValue, LoadingBlock, Page, PageHeader } from "../components/ui";
import { useFormat } from "../lib/format";
import { useLastDefined } from "../lib/hooks";
import { useAuditLogs } from "../lib/queries";

const ENTITY_TYPES = [
  "app",
  "api_key",
  "credential",
  "credential_profile",
  "profile_credential",
  "app_credential",
  "provider",
  "settings",
  "customer",
  "subscription",
  "user",
] as const;

const ACTION_TONE: Record<string, "success" | "accent" | "danger" | "warning" | "default"> = {
  create: "success",
  update: "accent",
  delete: "danger",
  erase: "danger",
  revoke: "warning",
  change_password: "default",
};

function changedKeys(a: AuditLog) {
  return [...new Set([...Object.keys(a.before ?? {}), ...Object.keys(a.after ?? {})])];
}

function show(value: unknown) {
  if (value === undefined) return "—";
  // An empty string would look like a missing value, so show it quoted.
  if (typeof value === "string") return value === "" ? '""' : value;
  return JSON.stringify(value);
}

function useLabels() {
  const { t, i18n } = useTranslation();
  return {
    action: (a: string) => (i18n.exists(`audit.actions.${a}`) ? t(`audit.actions.${a}` as never) : a),
    entity: (e: string) => (i18n.exists(`audit.entities.${e}`) ? t(`audit.entities.${e}` as never) : e),
  };
}

export function AuditLogPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const label = useLabels();
  const [params, setParams] = useSearchParams();
  const entity = params.get("type") ?? undefined;
  const openId = params.get("entry");
  const logs = useAuditLogs(entity);
  const rows = logs.data?.pages.flatMap((p) => p.items) ?? [];
  const open = rows.find((r) => r.id === openId) ?? null;
  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  return (
    <Page>
      <PageHeader title={t("audit.title")} description={t("audit.subtitle")} />
      <div className="flex">
        <FilterSelect
          label={t("audit.filter")}
          allLabel={t("audit.allTypes")}
          value={entity}
          onChange={(v) => update({ type: v, entry: undefined })}
          options={ENTITY_TYPES.map((e) => ({ id: e, label: label.entity(e) }))}
          className="w-full sm:w-64"
        />
      </div>
      {logs.isPending ? (
        <LoadingBlock />
      ) : logs.isError ? (
        <ErrorBlock error={logs.error} onRetry={() => logs.refetch()} />
      ) : rows.length === 0 ? (
        <Card>
          <EmptyBlock icon={ClockArrowRotateLeft} title={t("audit.empty")} />
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          <Table>
            <Table.ScrollContainer>
              <Table.Content
                aria-label={t("audit.title")}
                className="min-w-[760px]"
                onRowAction={(key) => update({ entry: String(key) })}
              >
                <Table.Header>
                  <Table.Column isRowHeader>{t("audit.when")}</Table.Column>
                  <Table.Column>{t("audit.actor")}</Table.Column>
                  <Table.Column>{t("audit.action")}</Table.Column>
                  <Table.Column>{t("audit.changes")}</Table.Column>
                </Table.Header>
                <Table.Body>
                  {rows.map((a) => {
                    const keys = changedKeys(a);
                    return (
                      <Table.Row key={a.id} id={a.id} className="cursor-pointer">
                        <Table.Cell className="whitespace-nowrap">
                          <span className="flex flex-col">
                            <span className="text-sm">{fmt.relative(a.created_at)}</span>
                            <span className="text-xs text-muted">{fmt.dateTime(a.created_at)}</span>
                          </span>
                        </Table.Cell>
                        <Table.Cell className="text-sm">{a.actor_label ?? a.actor_type}</Table.Cell>
                        <Table.Cell>
                          <span className="flex flex-wrap items-center gap-2">
                            <Chip size="sm" variant="soft" color={ACTION_TONE[a.action] ?? "default"}>
                              {label.action(a.action)}
                            </Chip>
                            <span className="text-sm">{label.entity(a.entity_type)}</span>
                          </span>
                        </Table.Cell>
                        <Table.Cell className="max-w-[360px]">
                          <span className="block truncate text-sm text-muted">
                            {keys.length ? keys.slice(0, 5).join(", ") : "—"}
                            {keys.length > 5 ? ` +${keys.length - 5}` : ""}
                          </span>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
          {logs.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                isPending={logs.isFetchingNextPage}
                onPress={() => logs.fetchNextPage()}
              >
                {t("common.loadMore")}
              </Button>
            </div>
          ) : null}
        </div>
      )}
      <AuditDrawer entry={open} onClose={() => update({ entry: undefined })} />
    </Page>
  );
}

function AuditDrawer({ entry, onClose }: { entry: AuditLog | null; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const label = useLabels();
  const shown = useLastDefined(entry);
  const keys = shown ? changedKeys(shown) : [];
  return (
    <Drawer.Backdrop isOpen={entry !== null} onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content placement="right">
        <Drawer.Dialog className="flex h-full flex-col" style={{ width: "34rem" }}>
          <Drawer.CloseTrigger />
          <Drawer.Header className="pr-10">
            <Drawer.Heading>{t("audit.detail")}</Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body className="flex flex-col gap-6 text-foreground">
            {shown ? (
              <>
                <KeyValue
                  items={[
                    { label: t("audit.when"), value: fmt.dateTime(shown.created_at) },
                    { label: t("audit.actor"), value: shown.actor_label ?? shown.actor_type },
                    {
                      label: t("audit.action"),
                      value: `${label.action(shown.action)} · ${label.entity(shown.entity_type)}`,
                    },
                    ...(shown.entity_id
                      ? [{ label: t("common.id"), value: <Code>{shown.entity_id}</Code> }]
                      : []),
                    ...(shown.ip ? [{ label: t("audit.ip"), value: shown.ip }] : []),
                  ]}
                />
                {keys.length ? (
                  <div className="overflow-x-auto rounded-xl border border-separator">
                    <table className="w-full text-sm">
                      <thead className="bg-surface-secondary text-left text-xs text-muted">
                        <tr>
                          <th className="px-3 py-2 font-medium">{t("audit.field")}</th>
                          <th className="px-3 py-2 font-medium">{t("audit.before")}</th>
                          <th className="px-3 py-2 font-medium">{t("audit.after")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keys.map((k) => (
                          <tr key={k} className="border-t border-separator align-top">
                            <td className="px-3 py-2 font-mono text-xs">{k}</td>
                            <td className="px-3 py-2 font-mono text-xs break-all text-muted">
                              {show(shown.before?.[k])}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs break-all">
                              {show(shown.after?.[k])}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted">{t("audit.noDiff")}</p>
                )}
              </>
            ) : null}
          </Drawer.Body>
          <Drawer.Footer>
            <Button slot="close" variant="secondary">
              {t("common.close")}
            </Button>
          </Drawer.Footer>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}
